import { createTheme } from "@mui/material/styles";

// Layout heights root -> navbar / content
const rootHeight = "100vh";
const appNavBarHeight = "4rem"; // 64px
const promoBannerHeight = "40px"; // Height of PromoBanner component
const appBarAndBannerHeight = "104px"; // Combined height: AppBar (64px) + PromoBanner (40px)
const mobileSafeAreaMin = "20px"; // Minimum safe area inset for mobile devices

// Extend module '@mui/material/styles' to add new properties
declare module "@mui/material/styles" {
  interface Theme {
    customTransitions: {
      colorTransition: string;
    };
    customLayoutHeight: {
      rootHeight: string;
      appNavBarHeight: string;
      promoBannerHeight: string;
      appBarAndBannerHeight: string;
      mobileSafeAreaMin: string;
    };
    customTypography: {
      botMessage: {
        fontFamily: string;
        fontWeight: number;
        fontSize: number;
        lineHeight: string;
        letterSpacing: string | number;
      };
    };
    customComponents: {
      linkPill: {
        background: string;
        border: string;
        color: string;
        hoverBackground: string;
      };
    };
  }
  interface Palette {
    customColors: {
      positiveVariationColor: string;
      negativeVariationColor: string;
      newsPaperColor: string;
      disabledColor: string;
      mainLower: string;
      // Text colors
      textPrimary: string;
      textSecondary: string;
      textTertiary: string;
      textQuaternary: string;
      textDisabled: string;
      // Border colors
      borderLight: string;
      // Interactive states
      hoverBackground: string;
      // Status colors
      recordingRed: string;
      grayLight: string;
    };
  }
  // to work in sync with `createTheme`
  interface ThemeOptions {
    customTransitions?: {
      colorTransition?: string;
    };
    customLayoutHeight?: {
      rootHeight?: string;
      appNavBarHeight?: string;
      promoBannerHeight?: string;
      appBarAndBannerHeight?: string;
      mobileSafeAreaMin?: string;
    };
    customTypography?: {
      botMessage?: {
        fontFamily?: string;
        fontWeight?: number;
        fontSize?: number;
        lineHeight?: string;
        letterSpacing?: string | number;
      };
    };
    customComponents?: {
      linkPill?: {
        background?: string;
        border?: string;
        color?: string;
        hoverBackground?: string;
      };
    };
  }
  interface PaletteOptions {
    customColors?: {
      positiveVariationColor?: string;
      negativeVariationColor?: string;
      newsPaperColor?: string;
      disabledColor?: string;
      mainLower?: string;
      // Welcome view colors
      welcomeBackground?: string;
      welcomeTitleColor?: string;
      welcomeTextColor?: string;
      welcomeDisclaimerColor?: string;
      // Generic UI tokens
      chipBg?: string;
      inputBorder?: string;
      shadowLow?: string;
      // Text colors
      textPrimary?: string;
      textSecondary?: string;
      textTertiary?: string;
      textQuaternary?: string;
      textDisabled?: string;
      // Border colors
      borderLight?: string;
      // Interactive states
      hoverBackground?: string;
      // Status colors
      recordingRed?: string;
      grayLight?: string;
      // Icon colors
      iconInactive?: string;
    };
  }
}

const BaseTheme = createTheme({
  typography: {
    fontFamily: "Ringside Narrow"
  },
  customTypography: {
    botMessage: {
      fontFamily: "Ringside Narrow",
      fontWeight: 325,
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: 0
    }
  },
  palette: {
    mode: "light",
    primary: {
      main: "#0076A8",
      dark: "#0C5B7D"
    },
    secondary: {
      main: "#fa6375",
      contrastText: "#ffffff"
    },
    background: {
      default: "#ffffff"
    },
    text: {
      primary: "#000000",
      secondary: "#333333"
    },
    error: {
      main: "#ff0004"
    },
    success: {
      main: "#28a745"
    },
    customColors: {
      positiveVariationColor: "#027A48",
      negativeVariationColor: "#B42318",
      newsPaperColor: "#D8D8D8",
      disabledColor: "#a1a1a1",
      mainLower: "#2668f5",
      // Welcome view colors
      welcomeBackground: "#fff",
      welcomeTitleColor: "#002D53",
      welcomeTextColor: "#333333",
      welcomeDisclaimerColor: "#999999",
      // Generic UI tokens
      chipBg: "#F4F6F8",
      inputBorder: "#D8E0E8",
      shadowLow: "0 3px 6px rgba(0, 0, 0, 0.05)",
      // Text colors (centralized from hardcoded values)
      textPrimary: "#000000",
      textSecondary: "#333333",
      textTertiary: "#888888",
      textQuaternary: "#444444",
      textDisabled: "#8e8ea0",
      // Border colors
      borderLight: "#e5e5e5",
      // Interactive states
      hoverBackground: "rgba(0, 0, 0, 0.04)",
      // Status colors
      recordingRed: "#FF3333",
      grayLight: "#E5E5E5",
      // Icon colors
      iconInactive: "#555555"
    }
  },
  shape: {
    borderRadius: 5
  },
  customTransitions: {
    colorTransition: "all 150ms ease-in-out"
  },
  customLayoutHeight: {
    rootHeight: rootHeight,
    appNavBarHeight: appNavBarHeight,
    promoBannerHeight: promoBannerHeight,
    appBarAndBannerHeight: appBarAndBannerHeight,
    mobileSafeAreaMin: mobileSafeAreaMin
  },
  customComponents: {
    linkPill: {
      background: "#F4F6F8",
      border: "1px solid #D8E0E8",
      color: "#333333",
      hoverBackground: "#EAEFF5"
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none"
          },
          "&:focus": {
            boxShadow: "none"
          },
          "&:active": {
            boxShadow: "none"
          }
        },
        containedPrimary: ({ theme }) => ({
          borderRadius: 7,
          fontSize: 16,
          textTransform: "none",
          fontWeight: 600,
          backgroundColor: theme.palette.customColors.mainLower, // o theme.palette.primary.main
          "&:hover": {
            backgroundColor: theme.palette.customColors.mainLower
          }
        })
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Default color for Typography - use text.primary from palette
          color: theme.palette.text.primary
        })
      }
    }
  }
});

export default BaseTheme;
